import { fromPromise } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { dhGetAuth } from 'helpers/Firebase';

export const TOKEN_EXPIRED_REASON = 'TOKEN_EXPIRED';

/**
 * mp-catalog-service rejects a stale Firebase ID token with an UNAUTHENTICATED
 * error whose extensions.reason is TOKEN_EXPIRED. Apollo Server returns it as
 * HTTP 400, so Apollo Client surfaces it under networkError.result.errors
 * rather than graphQLErrors; both are checked here.
 */
export const isTokenExpiredError = ({ graphQLErrors, networkError }) => {
  const errors = graphQLErrors || networkError?.result?.errors || [];
  return errors.some((e) => e?.extensions?.code === 'UNAUTHENTICATED' && e?.extensions?.reason === TOKEN_EXPIRED_REASON);
};

/**
 * Apollo link that, on a TOKEN_EXPIRED error, force-refreshes the Firebase ID
 * token, re-sets the authorization header and retries the operation once.
 * Place it AFTER the auth link and BEFORE the terminating http link so the
 * retried request reaches the server with the refreshed header.
 *
 * @param {Object}   [options]
 * @param {Function} [options.onRefreshed] - Called with the fresh token (e.g. to update a store)
 */
export const createTokenExpiredLink = ({ onRefreshed } = {}) =>
  onError((errorResponse) => {
    const { graphQLErrors, networkError, operation, forward } = errorResponse;

    if (!isTokenExpiredError(errorResponse)) {
      if (networkError) console.log(`[Network error]: ${networkError}`);
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`));
      }
      return undefined;
    }

    // Retry at most once per operation to avoid a refresh loop.
    if (operation.getContext().tokenRefreshed) return undefined;

    const user = dhGetAuth().currentUser;
    if (!user) return undefined;

    return fromPromise(
      user.getIdToken(true).catch((e) => {
        console.error('[Auth] Failed to refresh Firebase ID token', e);
        return null;
      })
    ).flatMap((token) => {
      if (token) {
        if (onRefreshed) onRefreshed(token);
        operation.setContext(({ headers = {} }) => ({
          tokenRefreshed: true,
          headers: { ...headers, authorization: `Bearer ${token}` },
        }));
      } else {
        operation.setContext({ tokenRefreshed: true });
      }
      return forward(operation);
    });
  });
